import { type DayOfWeek } from '~/components/forms/DaysOfWeekInput'
import type {
  AgreementDiscountCreateParams,
  AgreementDiscountTierCreateParams,
  AgreementDiscountTierResponse,
  ProductResponse
} from '~/data/api/client/billing'
import { type AgreementEntityType, type AgreementLanguage, type AgreementType, type CollectionPolicy, type DemandChargeAllocationMethod, type EntityCountLevel, type LineItemGrouping, type PeakSaverSaveType, AGREEMENT_TIER_QUANTITY_SOURCES, type TierQuantitySource, USAGE_PRICING_TYPES } from '~/data/types/billing'
import type { BillingCurrency } from '~/data/types/currencies'

/** V1 agreement form only: category is FLEET | DRIVER (v1 API). Do not use for v2. */
export const AGREEMENT_CATEGORIES_V1 = ['FLEET', 'DRIVER'] as const
export type AgreementCategoryV1 = (typeof AGREEMENT_CATEGORIES_V1)[number]

/** V1 agreement form only: billing cycle includes REAL_TIME (v1 API). Do not use for v2. */
export const BILLING_CYCLES_V1 = ['REAL_TIME', 'DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY'] as const
export type BillingCycleV1 = (typeof BILLING_CYCLES_V1)[number]

export const DELINQUENCY_TEST_TYPES = ['OUTSTANDING_BALANCE', 'DAYS_PAST_DUE', 'NUM_OPEN_INVOICES'] as const
export type DelinquencyTestType = (typeof DELINQUENCY_TEST_TYPES)[number]

/**
 * Business rules tying collection policy to billing cycle:
 * - REAL_TIME billing must settle through Synop (SYNOP_COLLECTS)
 * - NONE billing has nothing to collect (REPORTING_ONLY)
 * - any other cycle leaves the choice to the user
 */
export function getForcedCollectionPolicy(billingCycle: string | undefined): CollectionPolicy | undefined {
  if (billingCycle === 'REAL_TIME') return 'SYNOP_COLLECTS'
  if (billingCycle === 'NONE') return 'REPORTING_ONLY'
  return undefined
}

/**
 * Business rule tying auto-charge to billing cycle:
 * - REAL_TIME billing charges as usage occurs and settles through Synop (SYNOP_COLLECTS),
 *   so payment is collected automatically — auto-charge is implicitly always on.
 * - any other cycle leaves the choice to the user.
 */
export function getForcedAutoCharge(billingCycle: string | undefined): boolean | undefined {
  if (billingCycle === 'REAL_TIME') return true
  return undefined
}

/**
 * Business rule tying line item grouping to billing cycle:
 * - REAL_TIME billing is always itemized per session (CTEP-compliant), so the API rejects any
 *   non-ATOMIC grouping. Updates must send ATOMIC explicitly: the API leaves omitted fields
 *   unchanged, so a stored grouping from a previous period cycle would otherwise fail the
 *   effective-combination validation when switching to REAL_TIME.
 * - any other cycle leaves the choice to the user.
 */
export function getForcedLineItemGrouping(billingCycle: string | undefined): LineItemGrouping | undefined {
  if (billingCycle === 'REAL_TIME') return 'ATOMIC'
  return undefined
}

/** Backend defaults when only the legacy peakSaverEnabled boolean is set: POWER at 4 kW. */
export const DEFAULT_PEAK_SAVER_SAVE_TYPE: PeakSaverSaveType = 'POWER'
export const DEFAULT_PEAK_SAVER_QUANTITY_KW = 4

/** Backend defaults for demand-charge allocation: coincident site-peak contribution at 100% pass-through. */
export const DEFAULT_DEMAND_CHARGE_ALLOCATION_METHOD: DemandChargeAllocationMethod = 'SITE_PEAK_CONTRIBUTION'
export const DEFAULT_DEMAND_CHARGE_PASS_THROUGH_PERCENTAGE = 100

export type MetadataEntryFormData = {
  key: string
  value: string
}

/**
 * Sentinel select value meaning "no override — follow the selected price's setting". MUI selects render an
 * empty-string value as a blank box, so the form uses an explicit sentinel instead of ''.
 */
export const TIER_QUANTITY_SOURCE_PRICE_DEFAULT = 'PRICE_DEFAULT' as const

/** What the tier quantity source select can hold: a concrete override or the "price default" sentinel. */
export type TierQuantitySourceSelection = TierQuantitySource | typeof TIER_QUANTITY_SOURCE_PRICE_DEFAULT

/**
 * Sentinel select value meaning "no explicit level — billing infers it from the product's selection shape"
 * (connectors when the product has connector selections, otherwise chargers).
 */
export const ENTITY_COUNT_LEVEL_AUTOMATIC = 'AUTOMATIC' as const

/** What the entity count level select can hold: an explicit level or the "automatic" sentinel. */
export type EntityCountLevelSelection = EntityCountLevel | typeof ENTITY_COUNT_LEVEL_AUTOMATIC

export type AgreementProductFormData = {
  id: string
  quantity?: number
  /** Optional per-assignment override for volume tier selection; PRICE_DEFAULT means "use the price default". */
  tierQuantitySource?: TierQuantitySourceSelection
  /** Billing level for ENTITY_COUNT volume tiers; AUTOMATIC means "infer from the product's attached assets". */
  entityCountLevel?: EntityCountLevelSelection
  priceId?: string
  effectiveFrom?: string | null
  requiresPrice?: boolean
  /**
   * Whether the selected product is ARCHIVED. Set by the line item once status is known; yup
   * requires `false` before Next/Submit so we never send an archived productId that billing-api
   * would reject with 400. `undefined` means status is still resolving.
   */
  isArchivedProduct?: boolean
}

/**
 * Usage (TOU / energy / tariff pass-through) products are billed against metered consumption; no
 * assignment-level configuration (price, quantity, tier source) applies. Volume products are
 * configurable and are intentionally excluded here.
 */
export function isUsagePricingType(pricingType?: string | null): boolean {
  return !!pricingType && (USAGE_PRICING_TYPES as readonly string[]).includes(pricingType)
}

/** Volume products bill period fees from tier brackets; the tier quantity source decides which quantity selects the tier. */
export function isVolumeBasedPricingType(pricingType?: string | null): boolean {
  return pricingType === 'VOLUME_BASED'
}

/**
 * Mirrors billing-api's AgreementProductUtil.resolveEffectiveTierQuantitySource: the assignment
 * override wins, then the selected price's default, then USAGE. Used to decide whether the
 * agreement quantity input is meaningful for a volume product.
 */
export function resolveEffectiveTierQuantitySource(
  override: TierQuantitySourceSelection | '' | undefined,
  productDetail: Pick<ProductResponse, 'prices' | 'volumePricing'> | undefined,
  priceId: string | undefined
): TierQuantitySource {
  if (override && override !== TIER_QUANTITY_SOURCE_PRICE_DEFAULT) {
    return override
  }
  const selectedPrice = productDetail?.prices?.find((price) => price.id === priceId)
  const priceSource = selectedPrice?.tierQuantitySource
  if (priceSource && isTierQuantitySource(priceSource)) {
    return priceSource
  }
  return productDetail?.volumePricing?.tierQuantitySource ?? 'USAGE'
}

function isTierQuantitySource(value: string): value is TierQuantitySource {
  return (AGREEMENT_TIER_QUANTITY_SOURCES as readonly string[]).includes(value)
}

export function createEmptyAgreementProductLine(): AgreementProductFormData {
  return {
    id: '',
    quantity: 1,
    tierQuantitySource: TIER_QUANTITY_SOURCE_PRICE_DEFAULT,
    entityCountLevel: ENTITY_COUNT_LEVEL_AUTOMATIC,
    priceId: '',
    effectiveFrom: ''
  }
}

export type AgreementFormData = {
  name: string
  partnerOrganizationId: string
  type: AgreementType
  category: AgreementCategoryV1
  billingCycle: BillingCycleV1
  collectionPolicy: CollectionPolicy
  /** Empty string means "not set": the invoice template's grouping (or the SITE_PRICE default) applies. */
  lineItemGrouping: LineItemGrouping | ''
  startDate: string
  endDate?: string | null
  autoCharge: boolean
  invoiceDueDays?: number
  billingAnchorDayOfMonth?: number
  billingAnchorDayOfWeek?: DayOfWeek
  authorizationChargeEnabled: boolean
  authorizationCharge?: number
  peakSaverEnabled: boolean
  peakSaverSaveType?: PeakSaverSaveType
  peakSaverQuantity?: number
  demandChargeAllocationEnabled: boolean
  demandChargeAllocationMethod?: DemandChargeAllocationMethod
  demandChargePassThroughPercentage?: number
  delinquencyEnforcementEnabled: boolean
  delinquencyTestType?: DelinquencyTestType
  delinquencyThreshold?: number
  initialPrepaymentAmount?: number
  topOffThreshold?: number
  primaryContactName: string
  primaryContactEmail: string
  invoiceTemplateId?: string
  language: AgreementLanguage
  products: AgreementProductFormData[]
  discounts: DiscountFormData[]
  commitments: CommitmentFormData[]
  authorizedFleets?: string[]
  authorizedVehicles?: string[]
  authorizedUsers?: string[]
  authorizedUserGroups?: string[]
  authorizedAuthTags?: string[]
  prepaymentEnabled: boolean
  volumeDiscountEnabled: boolean
  usageCommitmentEnabled: boolean
  settlementCurrency: BillingCurrency
  metadataEntries: MetadataEntryFormData[]
}

export type AuthorizedEntityFormData = {
  entityId: string
  entityType: AgreementEntityType
}

/** The discount applications billing-api accepts, as the generated client types them; the tuple is what the select and the schema list. */
export type DiscountApplication = NonNullable<AgreementDiscountCreateParams['application']>
export const DISCOUNT_APPLICATIONS = ['VOLUME', 'FIRST_UNITS', 'COURTESY'] as const satisfies readonly DiscountApplication[]

export const DISCOUNT_QUANTITY_UNITS = ['KWH', 'MINUTES'] as const
export type DiscountQuantityUnit = (typeof DISCOUNT_QUANTITY_UNITS)[number]

export type DiscountTierFormData = {
  id?: string
  discountAmount?: number
  usageAmountStart?: number
  usageAmountEnd?: number | null
}

export type DiscountFormData = {
  name?: string
  productId?: string
  application?: DiscountApplication
  quantityUnit?: DiscountQuantityUnit
  tiers: DiscountTierFormData[]
  /**
   * COURTESY only (DRIVER agreements): the quantity per period on the house, in `quantityUnit`, and the percentage
   * taken off it.
   */
  courtesyDailyQuantity?: number
  courtesyWeeklyQuantity?: number
  courtesyPercent?: number
  courtesyWeekStartDay?: DayOfWeek
}

/** The periods a COURTESY tier's quantity resets over, as the generated client types them. */
export type DiscountPeriod = NonNullable<AgreementDiscountTierCreateParams['period']>
export const DISCOUNT_PERIODS = ['DAY', 'WEEK'] as const satisfies readonly DiscountPeriod[]

/** Backend default for a courtesy discount's week: the weekly quantity resets on Monday at 00:00 site-local time. */
export const DEFAULT_COURTESY_WEEK_START_DAY: DayOfWeek = 'MONDAY'
/** Backend default percentage on a courtesy discount: the covered units are on the house. */
export const DEFAULT_COURTESY_PERCENT = 100

/** Day labels shared by the weekly billing anchor and the courtesy week start, keyed so the lookup stays a literal union. */
export const DAY_OF_WEEK_LABEL_KEYS = {
  MONDAY: 'AgreementTermsStep_daysOfWeek_monday',
  TUESDAY: 'AgreementTermsStep_daysOfWeek_tuesday',
  WEDNESDAY: 'AgreementTermsStep_daysOfWeek_wednesday',
  THURSDAY: 'AgreementTermsStep_daysOfWeek_thursday',
  FRIDAY: 'AgreementTermsStep_daysOfWeek_friday',
  SATURDAY: 'AgreementTermsStep_daysOfWeek_saturday',
  SUNDAY: 'AgreementTermsStep_daysOfWeek_sunday'
} as const satisfies Record<DayOfWeek, string>

/** The courtesy quantity a COURTESY discount caps for one period, from its period tier. */
export function courtesyQuantityFor(
  tiers: readonly AgreementDiscountTierResponse[] | undefined,
  period: DiscountPeriod
): number | undefined {
  const tier = tiers?.find((candidate) => candidate.period === period)
  return tier?.endQuantity ?? undefined
}

export type CommitmentFormData = {
  name?: string
  productId?: string
  usageAmount?: number
  rateAboveCommitment?: number
}
